import React from 'react';
import { Lightbulb, CheckCircle2, MinusCircle, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// How each decision on a Meta suggestion is shown (decided by our rules and AI; see services/suggestions.py).
const DECISIONS = {
  applied: { label: 'Applied', icon: CheckCircle2, color: 'bg-money-soft text-money border-money/40' },
  skipped: { label: 'Skipped', icon: MinusCircle, color: 'bg-zinc-500/10 text-muted-foreground border-zinc-500/30' },
  failed: { label: "Couldn't apply", icon: AlertTriangle, color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' },
};

// Meta's "spend more" suggestions are never applied for the customer; they can add budget themselves.
const MORE_BUDGET = ['SCALE_GOOD_CAMPAIGN', 'BUDGET_LIMITED'];

export default function MetaSuggestions({ campaign, onAddBudget }) {
  const items = [...(campaign.meta_suggestions || [])].reverse(); // newest first
  if (!items.length && campaign.status !== 'live') return null;
  return (
    <div className="p-4 rounded-xl bg-card border-2 border-ink" data-testid="meta-suggestions">
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-muted-foreground" /> Meta&apos;s suggestions
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Meta sometimes suggests changes to improve your ad. We check each one against your ad and how it&apos;s doing,
        apply the ones that help, and never spend more than your budget.
      </p>
      {!items.length ? (
        <p className="text-xs text-muted-foreground text-center py-2">No suggestions from Meta yet. We check every 30 minutes.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map(s => {
            const d = DECISIONS[s.decision] || DECISIONS.skipped;
            const Icon = d.icon;
            return (
              <li key={s.signature} className="p-2.5 rounded-lg bg-butter/40 border-2 border-ink/15 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium">{s.label}</span>
                  <Badge variant="outline" className={`shrink-0 text-[10px] px-2 py-0.5 ${d.color}`}>
                    <Icon className="w-3 h-3 mr-1" /> {d.label}
                  </Badge>
                </div>
                {(s.lift || s.score_lift > 0) && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[s.lift, s.score_lift > 0 && `+${s.score_lift} opportunity score`].filter(Boolean).join(' · ')}
                  </p>
                )}
                {s.reason && <p className="text-xs mt-1">{s.reason}</p>}
                {onAddBudget && MORE_BUDGET.includes(s.type) && (
                  <button type="button" onClick={onAddBudget} className="mt-1.5 text-xs font-semibold underline underline-offset-2" data-testid="suggestion-add-budget">
                    Add budget
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
